import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { readWithTimeout } from '../lib/supabaseRead';
import { SIGNUP_FROM_SHARE_KEY } from '../lib/deepLinks';
import DAPublicPreview from './DAPublicPreview';
import LoadingScreen from './LoadingScreen';

export default function DADeepLink({ appNumber, council }) {
  const { session, loading: authLoading } = useAuth();
  const [da, setDa]           = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    (async () => {
      if (session) {
        // ── Logged-in: fetch full record and redirect to dashboard ──────────
        // Timeout-guarded: a shared link must never sit on an endless LoadingScreen.
        const selectFields = 'id, council_application_number, da_number, council';
        const [councilRef, daRef] = await Promise.all([
          readWithTimeout(
            signal => supabase
              .from('da_browse_view')
              .select(selectFields)
              .eq('council_application_number', appNumber)
              .ilike('council', `%${council}%`)
              .limit(1)
              .maybeSingle()
              .abortSignal(signal),
            { label: 'deeplink_council_ref' }
          ),
          readWithTimeout(
            signal => supabase
              .from('da_browse_view')
              .select(selectFields)
              .eq('da_number', appNumber)
              .ilike('council', `%${council}%`)
              .limit(1)
              .maybeSingle()
              .abortSignal(signal),
            { label: 'deeplink_da_ref' }
          ),
        ]);
        const lookupTimedOut = councilRef.error?.isTimeout || daRef.error?.isTimeout;
        const data = councilRef.data || daRef.data;

        if (data) {
          // Handle signup_from_share attribution
          const signupRaw = (() => {
            try { return sessionStorage.getItem(SIGNUP_FROM_SHARE_KEY) || localStorage.getItem(SIGNUP_FROM_SHARE_KEY); }
            catch { return null; }
          })();
          if (signupRaw) {
            try {
              const { appNumber: sAppNum, council: sCouncil } = JSON.parse(signupRaw);
              supabase.from('share_events').insert({
                event_type: 'signup_from_share',
                council_application_number: sAppNum,
                council: sCouncil,
                viewer_id: session.user.id,
              }).then(({ error }) => { if (error) console.warn('[DADeepLink] share_events insert failed:', error.message); });
            } catch {}
            try { sessionStorage.removeItem(SIGNUP_FROM_SHARE_KEY); } catch {}
            try { localStorage.removeItem(SIGNUP_FROM_SHARE_KEY); } catch {}
          }

          const pendingDeepLink = JSON.stringify({
            daId:      data.id,
            appNumber: data.council_application_number || data.da_number,
            council:   data.council,
          });
          try { sessionStorage.setItem('ss_pending_deeplink', pendingDeepLink); } catch {}
          try { localStorage.setItem('ss_pending_deeplink', pendingDeepLink); } catch {}
          window.location.replace('/');
        } else if (lookupTimedOut) {
          setLoadFailed(true);
          setFetching(false);
        } else {
          setNotFound(true);
          setFetching(false);
        }
      } else {
        // ── Not logged in: log preview_view and show public preview ─────────
        supabase.from('share_events').insert({
          event_type:                  'preview_view',
          council_application_number:  appNumber,
          council,
          referrer_url:                document.referrer || null,
          user_agent:                  navigator.userAgent,
        }).then(({ error }) => { if (error) console.warn('[DADeepLink] share_events insert failed:', error.message); });

        const { data: rows, error } = await readWithTimeout(
          signal => supabase.rpc('get_da_preview', {
            app_number:   appNumber,
            council_name: council,
          }).abortSignal(signal),
          { label: 'deeplink_preview' }
        );

        if (error?.isTimeout) {
          setLoadFailed(true);
        } else if (!error && rows && rows.length > 0) {
          setDa(rows[0]);
        } else {
          setNotFound(true);
        }
        setFetching(false);
      }
    })();
  }, [authLoading, session, appNumber, council]);

  if (authLoading || (fetching && !notFound && !loadFailed)) return <LoadingScreen />;

  if (loadFailed) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0a0f1a', display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '32px 16px', fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📡</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#e8e0d8', marginBottom: 8 }}>
          Connection is slow
        </div>
        <div style={{ fontSize: 14, color: '#9ca3af', textAlign: 'center', maxWidth: 320, marginBottom: 24, lineHeight: 1.6 }}>
          We couldn't load this DA just now. Check your connection and try again.
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 24px', background: '#d4a84a', color: '#0a0f1a',
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0a0f1a', display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '32px 16px', fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#e8e0d8', marginBottom: 8 }}>
          DA not found
        </div>
        <div style={{ fontSize: 14, color: '#9ca3af', textAlign: 'center', maxWidth: 320, marginBottom: 24, lineHeight: 1.6 }}>
          This DA may have been removed, or the link is incorrect.
        </div>
        <button
          onClick={() => window.location.replace('/')}
          style={{
            padding: '10px 24px', background: '#d4a84a', color: '#0a0f1a',
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  if (!session && da) {
    return <DAPublicPreview da={da} appNumber={appNumber} council={council} />;
  }

  return <LoadingScreen />;
}
